import { useEffect, useState } from "react";
import { Search, Sparkles, Loader2 } from "lucide-react";
import { getMateria, aiMateriaSearch } from "../lib/api";
import { motion } from "framer-motion";

export default function MateriaMedica() {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getMateria().then(setAll);
  }, []);

  const filtered = all.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      r.name.toLowerCase().includes(s) ||
      r.planet.toLowerCase().includes(s) ||
      r.key_symptoms.toLowerCase().includes(s) ||
      r.mind.toLowerCase().includes(s)
    );
  });

  const runAiSearch = async () => {
    if (!q.trim()) return;
    setAiLoading(true);
    setAiResult(null);
    try {
      const res = await aiMateriaSearch(q);
      setAiResult(res);
    } catch {
      setAiResult({ matches: [], explanation: "AI search failed. Try again." });
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div data-testid="page-materia">
      <section className="pt-16 pb-8">
        <div className="container-page">
          <div className="eyebrow mb-4">Reference</div>
          <h1 className="text-5xl md:text-6xl leading-tight max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Materia Medica Search
          </h1>
          <p className="mt-6 text-lg text-[#475569] max-w-2xl">Search Dr. Misra's curated Astrohomeopathic corpus by remedy, symptom, mind state, or planet. Use the AI search below for semantic matching on your symptom picture.</p>
        </div>
      </section>

      <section className="pb-12">
        <div className="container-page">
          <div className="glass-card p-4 flex items-center gap-3">
            <Search size={20} className="text-[#0EA5E9] ml-2 shrink-0" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runAiSearch()}
              data-testid="materia-search-input"
              placeholder="Try: 'anxiety with restlessness after midnight' or 'Pulsatilla'"
              className="flex-1 bg-transparent py-2 outline-none text-[#0F172A] placeholder:text-[#475569]"
            />
            <button
              onClick={runAiSearch}
              disabled={aiLoading || !q.trim()}
              data-testid="materia-ai-search-btn"
              className="btn-gold py-2 px-5 text-sm shrink-0 disabled:opacity-60"
            >
              {aiLoading ? <Loader2 className="animate-spin" size={16} /> : <><Sparkles size={16} className="mr-1.5" /> AI Search</>}
            </button>
          </div>

          {aiResult && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-3xl border border-[#D4AF37]/30 bg-[#FEF9E7] p-6" data-testid="materia-ai-result">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-[#D4AF37]" />
                <span className="text-xs tracking-widest uppercase text-[#D4AF37]">AI Analysis</span>
              </div>
              <p className="text-[#0F172A] mb-4">{aiResult.explanation}</p>
              {aiResult.matches?.length > 0 && (
                <div className="grid md:grid-cols-2 gap-3">
                  {aiResult.matches.map((m, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        const found = all.find((r) => r.name === m.remedy);
                        if (found) setSelected(found);
                      }}
                      className="text-left rounded-2xl bg-white p-4 border border-slate-100 hover:border-[#D4AF37] transition"
                    >
                      <div className="font-medium text-[#0F172A]">{m.remedy}</div>
                      <div className="text-xs text-[#0EA5E9] mt-0.5">{m.planet}</div>
                      <div className="text-sm text-[#475569] mt-2">{m.relevance}</div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-page">
          <div className="text-xs tracking-widest uppercase text-[#0EA5E9] mb-4">{filtered.length} remedies</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => (
              <button
                key={r.id}
                data-testid={`remedy-card-${r.id}`}
                onClick={() => setSelected(r)}
                className="text-left rounded-3xl border border-slate-100 bg-white p-6 hover:border-[#D4AF37] hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{r.name}</h3>
                    <div className="text-xs tracking-wider uppercase text-[#D4AF37] mt-1">{r.planet}</div>
                  </div>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed line-clamp-3">{r.key_symptoms}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-10" onClick={(e) => e.stopPropagation()} data-testid="remedy-detail-modal">
            <div className="text-xs tracking-widest uppercase text-[#D4AF37]">{selected.planet}</div>
            <h2 className="text-4xl mt-2 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{selected.name}</h2>
            <div className="space-y-5 text-[#0F172A]">
              <div><div className="text-xs tracking-widest uppercase text-[#0EA5E9] mb-1">Key Symptoms</div><p className="text-[#475569]">{selected.key_symptoms}</p></div>
              <div><div className="text-xs tracking-widest uppercase text-[#0EA5E9] mb-1">Mind</div><p className="text-[#475569]">{selected.mind}</p></div>
              <div><div className="text-xs tracking-widest uppercase text-[#0EA5E9] mb-1">Modalities</div><p className="text-[#475569]">{selected.modalities}</p></div>
            </div>
            <button onClick={() => setSelected(null)} data-testid="remedy-detail-close" className="btn-sky mt-8">Close</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
